import { useMemo } from 'react';

import type { InstantSurvey, InstantSurveyType } from '@api/instant_survey/types';

import QuestionnaireDisplay from '../../../../../../../../components/QuestionnaireDisplay';
import { PreviewButton } from '../../../../../../../../components/SurveyPreview';
import {
  getQuestionDisplayIndexById,
  getQuestionsCost,
} from '../../../../../../../../pages/CreateInstantSurvey/utilities/helpers';
import {
  SurveyContext,
  type SurveyContextType,
} from '../../../../../../../../pages/CreateInstantSurvey/utilities/context';

import styles from './_index.module.scss';

interface QuestionnaireProps {
  canPreviewSurvey: boolean;
  isPreviewButtonDisabled: boolean;
  onPreviewButtonClick: () => void;
  survey: InstantSurvey;
  surveyType: InstantSurveyType;
}

const noop = () => {};
const asyncNoop = async () => {};

const Questionnaire = ({
  canPreviewSurvey,
  isPreviewButtonDisabled,
  onPreviewButtonClick,
  survey,
  surveyType,
}: QuestionnaireProps) => {
  const activeGroups = useMemo(
    () => (survey.groups || []).filter((group) => !group._delete),
    [survey.groups]
  );
  const activeQuestions = useMemo(
    () => (survey.questions || []).filter((question) => !question._delete),
    [survey.questions]
  );
  const questionDisplayIndexById = useMemo<Record<number, string>>(
    () => getQuestionDisplayIndexById(survey.questions || []) as Record<number, string>,
    [survey.questions]
  );

  const contextValue = useMemo<SurveyContextType>(
    () => ({
      activeGroups,
      activeQuestions,
      activeStep: 0,
      assignGroupToParent: asyncNoop,
      assignQuestionToGroup: asyncNoop,
      autoSaveError: null,
      copyGroup: async () => {},
      createGroupForQuestion: async () => {},
      currentQuestionCount: getQuestionsCost(activeQuestions),
      currentQuestionnairePrice: undefined,
      deleteGroup: async () => {},
      hasUnsavedChanges: false,
      insightsDocument: undefined,
      insightsDocumentType: {} as SurveyContextType['insightsDocumentType'],
      instantSurveyType: surveyType,
      isAutoSaving: false,
      isCopyingGroup: false,
      isLoadingSubscription: false,
      isReadOnly: true,
      isRealtimeSampleValidationEnabled: false,
      isResearchObjectivesTooLong: false,
      isSaveAsDraftOpen: false,
      isSavingAsDraft: false,
      isSubscribed: false,
      jobId: null,
      lastAutoSavedAt: null,
      launchMethod: null,
      maxQuestions: activeQuestions.length,
      persistSurveyDraft: async () => null,
      questionDisplayIndexById,
      renameGroup: async () => {},
      saveInstantSurvey: async () => survey,
      setActiveStep: noop,
      setInsightsDocument: noop,
      setIsSaveAsDraftOpen: noop,
      setIsSavingAsDraft: noop,
      setJobId: noop,
      setLaunchMethod: noop,
      setShowAIGenerationModal: noop,
      setShowSurveyTemplateModal: noop,
      setSurveyGoals: noop,
      showAIGenerationModal: false,
      showSurveyTemplateModal: false,
      subscription: null,
      subscriptionError: null,
      survey,
      surveyGoals: '',
      toggleGroupRandomize: async () => {},
      toggleQuestionAnchored: noop,
      updateSurvey: noop,
      validatingGroupClientIds: [],
    }),
    [activeGroups, activeQuestions, questionDisplayIndexById, survey, surveyType]
  );

  return (
    <div>
      <div className={styles['questionnaire-header']}>
        <h3>Questionnaire</h3>
        {canPreviewSurvey && (
          <PreviewButton
            isDisabled={isPreviewButtonDisabled}
            location="survey details"
            onClick={() => onPreviewButtonClick()}
          />
        )}
      </div>
      {activeQuestions.length > 0 ? (
        <SurveyContext.Provider value={contextValue}>
          <QuestionnaireDisplay
            canPreviewSurvey={canPreviewSurvey}
            mode="read"
            onPreviewSurvey={onPreviewButtonClick}
            questionDisplayIndexById={questionDisplayIndexById}
            questionLogic={survey.questionLogic || {}}
            questions={activeQuestions}
          />
        </SurveyContext.Provider>
      ) : (
        <p>No questions are assigned to this survey.</p>
      )}
    </div>
  );
};

export default Questionnaire;
